import React from 'react';
import { useInterval } from 'react-use';

type TimeCounterProps = {
    /** 计时类型：正计时/倒计时 */
    countType: 'up' | 'down';
    /** 计时起始时间（单位：秒） */
    startTime: number;
    /** 展示文案中是否展示“天” */
    needDay?: boolean;
    /** 计时中间节点，计时到此时间时触发对应回调 */
    middleTime?: number;
    /** 计时到中间节点时的回调函数 */
    onCountToMiddle?: () => void;
    /** 倒计时结束回调 */
    onCountToZero?: () => void;
    /** 格式化，不给定时使用默认格式“a天b小时c分d秒” */
    format?: (timeInSecounds: number) => string;
};

const { useState, useEffect, useMemo } = React;

const calculateTime = (timeInSecounds: number, needDay = false, needHour = true) => {
    let day = 0;
    let hour = 0;
    let minute = 0;
    let second = 0;

    if (needDay) {
        day = Math.floor(timeInSecounds / (24 * 60 * 60));
        timeInSecounds %= 24 * 60 * 60;
    }

    if (needHour) {
        hour = Math.floor(timeInSecounds / (60 * 60));
        timeInSecounds %= 60 * 60;
    }

    minute = Math.floor(timeInSecounds / 60);
    second = timeInSecounds % 60;

    return { day, hour, minute, second };
};

const padZero = (num: number) => {
    return num > 9 ? `${num}` : `0${num}`;
};

/**
 * 将秒数转换为时间字符串 形如 xx小时xx分xx秒
 */
const convertSecondsToTimeStr = (
    timeInSecounds: number,
    needDay = false,
    needZero = true,
): string => {
    const result = calculateTime(timeInSecounds, needDay);

    return `${needDay ? `${result.day}天` : ''}${
        !needZero && result.hour === 0 ? '' : `${result.hour}小时`
    }${!needZero && result.minute === 0 ? '' : `${result.minute}分`}${result.second}秒`;
};

/**
 * 将秒数转换为时间字符串 形如 xx:xx:xx
 */
const convertSecondsToTimeStrShort = (timeInSecounds: number): string => {
    const result = calculateTime(timeInSecounds, false);

    return `${padZero(result.hour)}:${padZero(result.minute)}:${padZero(result.second)}`;
};

/**
 * 将秒数转换为时间字符串
 * - 小于 1 小时展示 xx:xx
 * - 超过 1 小时展示 xx:xx:xx
 */
const convertSecondsToTimeStrShortWithNoZeroHour = (timeInSecounds: number): string => {
    const result = calculateTime(timeInSecounds, false);

    return result.hour
        ? `${padZero(result.hour)}:${padZero(result.minute)}:${padZero(result.second)}`
        : `${padZero(result.minute)}:${padZero(result.second)}`;
};

/*
 * 将秒数转换为时间字符串 形如 xx:xx
 */
const convertSecondsToTimeMinuteStrShort = (timeInSecounds: number): string => {
    const result = calculateTime(timeInSecounds, false, false);

    return `${padZero(result.minute)}:${padZero(result.second)}`;
};

const TimeCounter: React.FC<TimeCounterProps> = ({
    countType,
    startTime,
    needDay,
    middleTime,
    onCountToZero,
    onCountToMiddle,
    format,
}) => {
    /** 计时是否进行中 */
    const [counting, setCountingStatus] = useState(false);
    /** 内部维护的计数 */
    const [localTime, setLocalTime] = useState(0);
    /** 中间节点回调是否触发过 */
    // const [countToMiddleTriggered, setCountToMiddleTriggeredState] = useState(false);

    // useEffect(() => {
    //     if (
    //         middleTime &&
    //         ((countType === 'up' && startTime > middleTime) ||
    //             (countType === 'down' && startTime < middleTime))
    //     ) {
    //         setCountToMiddleTriggeredState(true);
    //     } else {
    //         setCountToMiddleTriggeredState(false);
    //     }
    // }, [countType, startTime, middleTime]);

    useEffect(() => {
        // 重置计时状态
        setCountingStatus(!(countType === 'down' && startTime <= 0));
        // 重置内部计数器
        setLocalTime(startTime);

        // 如果是倒计时且初始值就是负数，直接执行回调
        if (countType === 'down' && startTime <= 0 && onCountToZero) {
            onCountToZero();
        }
    }, [countType, startTime, onCountToZero]);

    useInterval(
        () => {
            const currentTime = countType === 'up' ? localTime + 1 : localTime - 1;
            if (middleTime && currentTime === middleTime) {
                if (onCountToMiddle) {
                    onCountToMiddle();
                }
                // setCountToMiddleTriggeredState(true);
            }

            setLocalTime(currentTime);

            if (countType === 'down' && currentTime === 0) {
                setCountingStatus(false);
                if (onCountToZero) {
                    onCountToZero();
                }
            }
        },
        counting ? 1000 : null,
    );

    const finalFormat = useMemo(
        () => (typeof format === 'function' ? format : convertSecondsToTimeStr),
        [format],
    );

    return <>{finalFormat(localTime, needDay)}</>;
};

export {
    convertSecondsToTimeStr,
    convertSecondsToTimeStrShort,
    convertSecondsToTimeStrShortWithNoZeroHour,
    convertSecondsToTimeMinuteStrShort,
};
export default TimeCounter;
